import React from 'react';
import { useTheme } from '@mui/material';
import BaseChart from './BaseChart';

/**
 * TopModelsChart Component
 *
 * Displays top 5 models using a horizontal bar chart with:
 * - Model execution counts
 * - Color-coded bars
 * - Interactive tooltips
 * - Responsive design
 */
const TopModelsChart = ({ data, period, loading = false }) => {
  const theme = useTheme();

  if (loading || !data || !data.top_models || data.top_models.length === 0) {
    return (
      <BaseChart
        title={`Top 5 Models (${period || 'N/A'})`}
        subtitle="Most used models by execution count"
        type="bar"
        series={[]}
        height={400}
        loading={loading}
      />
    );
  }

  // Use theme colors for chart - primary, success, warning, error, secondary
  const chartColors = [
    theme.palette.primary.main,
    theme.palette.success.main,
    theme.palette.warning.main,
    theme.palette.error.main,
    theme.palette.secondary.main,
  ];

  const chartOptions = {
    chart: {
      type: 'bar',
      height: 400,
    },
    plotOptions: {
      bar: {
        horizontal: true,
        barHeight: '60%',
        borderRadius: 4,
        distributed: true, // Each bar gets its own color
        dataLabels: {
          position: 'left',
        },
      },
    },
    colors: chartColors,
    dataLabels: {
      enabled: true,
      formatter: function (val) {
        return val ? val.toLocaleString() : '0';
      },
      style: {
        fontSize: '11px',
        fontWeight: 'bold',
        colors: ['#fff'],
      },
    },
    xaxis: {
      labels: {
        style: {
          fontSize: '11px',
        },
        formatter: function (val) {
          return val ? val.toLocaleString() : '0';
        },
      },
      title: {
        text: 'Executions',
        style: {
          fontSize: '12px',
          fontWeight: 600,
        },
      },
    },
    yaxis: {
      categories: data.top_models.map(model => model.model_name || 'Unknown'),
      labels: {
        show: false, // Hide Y-axis labels
      },
      title: {
        text: 'Models',
        style: {
          fontSize: '12px',
          fontWeight: 600,
        },
      },
    },
    legend: {
      show: true,
      position: 'right',
      horizontalAlign: 'right',
      customLegendItems: data.top_models.map(
        model => model.model_name || 'Unknown'
      ),
      markers: {
        fillColors: chartColors.slice(0, data.top_models.length), // Match colors to models
      },
      formatter: function (seriesName, opts) {
        // Return model name based on data point index
        const index = opts.dataPointIndex;
        if (index !== undefined && data.top_models && data.top_models[index]) {
          return data.top_models[index].model_name || 'Unknown';
        }
        return seriesName;
      },
    },
    tooltip: {
      enabled: true,
      fixed: {
        enabled: true,
        position: 'bottomLeft', // Position tooltip at bottom left
        offsetX: 0,
        offsetY: 0,
      },
      custom: function ({ series, seriesIndex, dataPointIndex, w }) {
        const model = data.top_models[dataPointIndex];
        if (!model) {
          return '<div>No data available</div>';
        }

        return `
          <div style="padding: 12px; background: white; border: 1px solid #e0e0e0; border-radius: 8px; box-shadow: 0 4px 6px rgba(0,0,0,0.1); min-width: 280px; z-index: 1000;">
            <div style="font-weight: bold; margin-bottom: 8px; color: ${theme.palette.primary.main}; font-size: 14px; word-wrap: break-word;">${model.model_name}</div>
            <div style="margin-bottom: 4px;">Executions: <strong>${model.executions.toLocaleString()}</strong></div>
            <div style="margin-bottom: 4px;">Success Rate: <strong>${model.success_rate.toFixed(1)}%</strong></div>
            <div style="margin-bottom: 4px;">Total Cost: <strong>$${model.total_cost.toFixed(4)}</strong></div>
            <div style="margin-bottom: 4px;">Avg Cost per Execution: <strong>$${model.avg_cost_per_execution.toFixed(4)}</strong></div>
            <div style="margin-bottom: 4px;">Avg Execution Time: <strong>${model.avg_execution_time.toFixed(2)}s</strong></div>
          </div>
        `;
      },
    },
    responsive: [
      {
        breakpoint: 768,
        options: {
          chart: {
            height: 350,
          },
          xaxis: {
            labels: {
              style: {
                fontSize: '9px',
              },
            },
          },
          yaxis: {
            labels: {
              style: {
                fontSize: '9px',
              },
            },
          },
        },
      },
    ],
  };

  const series = [
    {
      name: 'Executions',
      data: data.top_models.map(model => model.executions),
    },
  ];

  return (
    <BaseChart
      title={`Top 5 Models (${period || 'N/A'})`}
      subtitle="Most used models by execution count"
      type="bar"
      series={series}
      options={chartOptions}
      height={400}
      loading={loading}
    />
  );
};

export default TopModelsChart;
